/**
 * Typography tokens — sizes + line-heights used by forms, step titles, and dense tables.
 *
 * --------------------------------------------------------------------------
 * Why line heights look “random” (14 → 20, 15 → 22, etc.)
 * --------------------------------------------------------------------------
 * Line height is **not** the font size—it’s the total vertical slot for each line of text.
 * If line height equals font size, letters feel cramped (especially wrapped labels).
 * If line height is too tall, forms waste vertical space and mobile users scroll forever.
 *
 * These pairs were tuned for **readability inside bordered inputs** and **wrapped field labels**
 * while staying close to what the legacy Create Request screens already shipped. When design updates the global
 * type scale, change **both** `fontSize` and `lineHeight` together so nothing clips.
 * --------------------------------------------------------------------------
 *
 * Visual Template reference (`881:7557`): Roboto scale on Figma; we mirror key sizes where merged.
 */
export const fontSize = {
  pageTitle: 24,
  /** Form step title (Create/Edit Request) — same size as `pageTitle` */
  formStepPageTitle: 24,
  sectionTitle: 18,
  subheading: 16,
  formLabel: 14,
  /** Single-line input / closed select value (legacy **14px** in **48px** control). */
  formControl: 14,
  /** UserProfile `fieldLabel`: 13, uppercase, letterSpacing 0.5 */
  fieldLabelUppercase: 13,
  body: 15,
  charCount: 12,
  cardSubtitle: 13,
  button: 16,
  /** Native `SubsectionForm` header title when web uses {@link fontSize.sectionTitle} (18). */
  formSectionTitleCompact: 14,
  /** Attachment list date/size columns (`SubsectionForm` compact rows). */
  formAttachmentMeta: 11,
  /** Stacked attachment meta on small native (`SubsectionForm` `mobileMetaRow`). */
  formAttachmentMobileMeta: 10,
} as const;

export const lineHeight = {
  /** Default paragraph rhythm when we still use 15px body copy in places (extra ~7px breathing room). */
  body: 22,
  /** Field labels in dense forms are compact in legacy; 16 removes extra top/bottom air. */
  formLabel: 16,
  /** Text typed inside single-line inputs—matches label scale so placeholder vs value feels consistent. */
  formControl: 20,
  /** 18px card/section title (subsection headers). */
  sectionTitle: 24,
  /** 24px step title. */
  formStepPageTitle: 30,
  /** {@link fontSize.formSectionTitleCompact} — template paragraph small uses 20 for 12px; 14 + 8 for dense headers. */
  formSectionTitleCompact: 22,
  /** {@link fontSize.formAttachmentMeta} — 11 + 8. */
  formAttachmentMeta: 19,
  /** {@link fontSize.formAttachmentMobileMeta} — 10 + 8. */
  formAttachmentMobileMeta: 18,
  /**
   * {@link fontSize.charCount} — single-line “0 / 10000”; match font size so the row does not
   * inherit {@link body} (22px) and add dead space under multiline fields / card bottoms.
   */
  charCount: 12,
} as const;

export const fontWeight = {
  bold: '700',
  semibold: '600',
  medium: '500',
  /** Body weight. Named so a control that deliberately does NOT emphasise can say so. */
  regular: '400',
} as const;

export const letterSpacing = {
  fieldLabelUppercase: 0.5,
} as const;
